import { IconButton, Stack } from "@mui/material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import DateField from "./DateField";
import MultiSelect from "./MultiSelect";
import { SearchRequest } from "../../../api/customer";
import SelectField from "../../../components/atoms/SelectField";
import TextField from "../../../components/atoms/TextField";
import SearchIcon from "../../../components/icons/ui-actions/SearchIcon";
import {
  SearchState,
  useSearchStore,
  useSetSearchStore,
} from "../../../stores/formSearchStore";
import { usePermissionStore } from "../../../stores/permissionStore";
import { mono } from "../../../theme/colors";

interface SearchBarProps {
  request: SearchRequest;
  setIsSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setRequest: React.Dispatch<React.SetStateAction<SearchRequest>>;
}

export const country = [
  { value: "ALL", label: "All" },
  { value: "TH", label: "TH" },
  { value: "JP", label: "JP" },
  { value: "ID", label: "ID" },
  { value: "MY", label: "MY" },
  { value: "KH", label: "KH" },
  { value: "PH", label: "PH" },
  { value: "HK", label: "HK" },
  { value: "VN", label: "VN" },
  { value: "MO", label: "MO" },
  { value: "SG", label: "SG" },
  { value: "Others", label: "Others" },
];

const SearchBar = ({ request, setIsSearch, setRequest }: SearchBarProps) => {
  const SearchState = useSearchStore();
  const SearchStore = useSetSearchStore();
  const formMethods = useForm<SearchState>({
    defaultValues: SearchState,
  });
  const formValues = formMethods.getValues();
  const isHaveSignUpCountry = formMethods.watch("signUpCountry").length > 0;

  const { countries } = usePermissionStore();

  const optionsSelect = useMemo(() => {
    if (countries) {
      const allCountries = countries?.find(val => val === "ALL");
      if (allCountries) {
        return country;
      } else {
        return countries?.map(item => ({
          label: item,
          value: item,
        }));
      }
    }
  }, [countries]);

  const optionsSelectInsuranceCountry = useMemo(() => {
    const noneOptions = {
      label: "None",
      value: "",
    };
    if (optionsSelect) {
      const optionsAllInsuranceCountry = optionsSelect.find(
        val => val.value === "ALL",
      );
      if (optionsAllInsuranceCountry) {
        return [
          ...optionsSelect.filter(val => val.value !== "ALL"),
          noneOptions,
        ];
      } else {
        return [...optionsSelect, noneOptions];
      }
    }
    return optionsSelect;
  }, [countries]);

  const handleClickSearch = () => {
    const values = formMethods.getValues();
    if (!values.signUpCountry.length) {
      formMethods.setError("signUpCountry", {
        message: "This field is required",
        type: "required",
      });
      return;
    }
    setIsSearch(true);
    SearchStore(values);
    const value = {
      omneName: values.omneName ? values.omneName.trim() : undefined,
      dob: values.dob || undefined,
      phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : undefined,
      email: values.email ? values.email.trim() : undefined,
      policyNumber: values.policyNumber || undefined,
      insuranceCountry: values.insuranceCountry.toString() || undefined,
      signUpCountry:
        (values.signUpCountry.find(item => item === "ALL")
          ? "ALL"
          : values.signUpCountry.toString()) || undefined,
    };
    setRequest(prev => ({ ...prev, ...value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleClickSearch();
  };

  const colorHelpText = useMemo(() => {
    if (
      !formValues.omneName &&
      !formValues.dob &&
      !formValues.phoneNumber &&
      !formValues.email &&
      !formValues.policyNumber &&
      !formValues.insuranceCountry.length &&
      !!formMethods.formState.errors.signUpCountry?.message
    ) {
      return "#B30909";
    } else {
      return "#8B8E8F";
    }
  }, [formValues]);

  const clearDob = () => {
    formMethods.setValue("dob", "");
  };

  return (
    <Stack direction={"row"} justifyContent="center" spacing={2} paddingY={2}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit} data-testid="customer-search-form">
          <Stack marginBottom={0.5} color={colorHelpText}>
            ( It is mandatory to fill at least one of the optional fields below
            )
          </Stack>
          <Stack spacing={2} direction={"row"}>
            <Stack width={180}>
              <TextField
                name="omneName"
                label="Omne profile Name"
                disabled={!isHaveSignUpCountry}
              />
            </Stack>
            <Stack>
              <DateField
                name="dob"
                label="Omne DOB"
                disabled={!isHaveSignUpCountry}
              />
            </Stack>
            <Stack>
              <TextField
                name="phoneNumber"
                label="Omne Phone"
                disabled={!isHaveSignUpCountry}
              />
            </Stack>
            <Stack>
              <TextField
                name="email"
                label="Omne Email"
                disabled={!isHaveSignUpCountry}
              />
            </Stack>
            <Stack>
              <TextField
                name="policyNumber"
                label="Policy Number"
                disabled={!formMethods.watch("insuranceCountry").length}
              />
            </Stack>
            <Stack width={180}>
              <SelectField
                name="insuranceCountry"
                label="Insurance Country"
                options={optionsSelectInsuranceCountry || []}
                disabled={!isHaveSignUpCountry}
              />
            </Stack>
            <Stack width={210}>
              <MultiSelect
                name="signUpCountry"
                label="Omne Signup Country"
                options={optionsSelect || []}
              />
            </Stack>
            <Stack spacing={2} justifyContent={"flex-start"}>
              <IconButton
                data-testid="inventory-homepage_product-Search-search_button"
                onClick={handleClickSearch}
                sx={{
                  backgroundColor: mono.white,
                  width: 40,
                  height: 40,
                }}
              >
                <SearchIcon />
              </IconButton>
            </Stack>
          </Stack>
          <input type="submit" hidden />
        </form>
      </FormProvider>
    </Stack>
  );
};

export default SearchBar;
